import { MaterialApi, ISimplifiedVisualMaterial, IDatahubCase, IMaterial } from 'api';
import { useQuery } from '@tanstack/react-query';
import QUERIES from 'modules/common/queries';
import { useEffect, useMemo, useCallback, useState } from 'react';
import isEqual from 'lodash/isEqual';
import { arrayMove } from '@dnd-kit/sortable';
import { IMaterialList } from './MaterialList';
import queries from 'modules/common/queries';

const DEFAULT_MATERIAL_RESPONSE = {
  batchList: [],
  materials: [],
  links: [],
};

export const useVisualMaterials = (caseId?: IDatahubCase['id'], enabled = true) => {
  const {
    data = DEFAULT_MATERIAL_RESPONSE,
    isLoading,
    isFetched,
    isRefetching,
    isError,
    refetch
  } = useQuery({
    queryKey: [QUERIES.visualMaterialsSimplified, caseId],
    queryFn: async () => (caseId ? await MaterialApi.getSimplifiedVisualMaterials(caseId) : undefined),
    enabled: enabled && !!caseId,
  });
  const [innerVisualMaterials, setInnerVisualMaterials] = useState(data.materials || []);
  const [filteredMaterialIds, setFilteredMaterialIds] = useState<string[]>([]);

  const isSortOrderHasChanged = useMemo(
    () => !isEqual(data.materials, innerVisualMaterials),
    [data.materials, innerVisualMaterials]
  );

  useEffect(() => {
    if (data.materials) {
      setInnerVisualMaterials(data.materials);
    }
  }, [data]);

  const onSortEnd = ({ newIndex, oldIndex }: { newIndex: number; oldIndex: number }) => {
    document.body.style.cursor = 'default';
    setInnerVisualMaterials(arrayMove(innerVisualMaterials, oldIndex, newIndex));
  };

  const onSortCancel = () => setInnerVisualMaterials(data.materials || []);

  const selectMaterialItem: IMaterialList['onItemClick'] = useCallback(
    (materialId: IMaterial['id']) => {
      setInnerVisualMaterials(
        innerVisualMaterials.map(material => ({
          ...material,
          isSelected: material.id === materialId ? !material.isSelected : material.isSelected,
        }))
      );
    },
    [innerVisualMaterials]
  );

  const toggleSelectAllMaterials = useCallback(
    (selected: boolean) => {
      setInnerVisualMaterials(
        innerVisualMaterials.map(material => ({
          ...material,
          isSelected: selected ? (filteredMaterialIds.includes(material.id) ? true : material.isSelected) : false,
        }))
      );
    },
    [innerVisualMaterials, filteredMaterialIds]
  );

  const setActivationStatusForSelected = (materialIsActive: ISimplifiedVisualMaterial['isActive']) => {
    setInnerVisualMaterials(
      innerVisualMaterials.map(material => ({
        ...material,
        isActive: material.isSelected ? materialIsActive : material.isActive,
      }))
    );
  };

  const onDescriptionEdit = (
    description: ISimplifiedVisualMaterial['description'],
    materialId: ISimplifiedVisualMaterial['id']
  ) => {
    setInnerVisualMaterials(prev =>
      prev.map(material => ({
        ...material,
        description: material.id === materialId ? description : material.description,
      }))
    );
  };

  return {
    visualMaterials: innerVisualMaterials,
    isLoading,
    isFetched,
    isSortOrderHasChanged,
    onSortEnd,
    onSortCancel,
    onDescriptionEdit,
    selectMaterialItem,
    toggleSelectAllMaterials,
    setActivationStatusForSelected,
    setFilteredMaterialIds,
    filteredMaterialIds,
    batchList: data.batchList,
    externalLinks: data.links,
    refetch,
    isRefetching,
    isError
  };
};

export const useCollagePricing = (caseId?: IDatahubCase['id']) =>
  useQuery({
    queryKey: [queries.collagePricing, caseId],
    queryFn: async () => (caseId ? await MaterialApi.getCollagePrice(caseId) : undefined),
    enabled: !!caseId,
  });
